/**
 * V108-6-4 — the setup-steps list (enablers) on the Setup page. Design: docs/v10.8/setup-screens-design.md.
 *
 *   - Groups: Open · In progress · Done · Snoozed · Declined. No status column; glyphs only for exceptions.
 *   - Accordion: one row open at a time (the in-flight row, else the top open row, else ?step=<id>).
 *   - Observed progress only: Waiting → Done (what was read + when); failed shows the cause and its ONE
 *     fix link (failureCauseView, AC-5); nobody can mark a step done.
 *   - "Checked N s ago" ticks outside any live region (AC-6); a Check now result is announced once.
 *   - Send to someone (assignee), snooze vs decline (nothing preselected; reason required), reopen.
 *   - Trust disclosures (collapsed): the AWS role, how we hold an admin key, what the pull request does.
 *
 * Authorization is the server's: PATCH needs owner / admin / program lead (`canChange`); connecting,
 * consent and Check now need owner / admin (`approver`). Controls a viewer can't use are not rendered.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, CodeBlock, Field, Input, Select } from '@aigovops/design-system';
import { OPEN_SETUP_STEP, couldntCheckEntry, formatAwsAccount, getEnablerCatalogEntry, isCouldntCheckReason, vendorName } from '@aigovops/core';
import { fixActionFor } from '../../lib/couldnt-check';
import { listPostureConnectors, setPostureConnectorStatus } from '../../lib/api';
import { queryKeys } from '../../lib/query-keys';
import { CouldntCheckChip } from '../CouldntCheck';
import { isPostureFormKind } from '../PostureConnections';
import { onboardingHref } from '../../lib/onboarding-model';
import {
  declareAwsAccounts, patchEnabler, runConnectorCheck, setAwsAccountState, setConsentStarted, type EnablerItem, type EnablerPatch,
} from '../../lib/setup-api';
import { causeOf, checkResult, groupOf, groupSteps, GROUP_LABEL, GROUP_ORDER, isNewCiRun, movesNothing, personLabel, pickerPeople, type Person, type StepGroup } from '../../lib/setup-model';
import { ActorKind, evidenceNoun, ExtLink, Glyph, NotTakenChip, NotTakenLine, State, UnlockChips } from './parts';
import { useAnnouncer, useSetupToast } from './feedback';
import { CiTrustAction, CiTrustState, ciTrustLine, SigningHint } from './CiTrust';
import { EnvironmentChoice, GapActions } from './SetupGaps';

export interface ListViewer { user_id: string; role: string }

/** Where a console step finishes (opened in a new tab; "consent started" is stored on the server). */
export const CONSOLE_URL: Record<string, string> = {
  aws: 'https://console.aws.amazon.com/iam/home#/roles/create',
  gcp: 'https://console.cloud.google.com/iam-admin/workload-identity-pools',
};

/** The in-app link that starts a connection: the connect drawer on Setup, or onboarding's Grant step. */
export function connectHref(slug: string, kind: string): string {
  return isPostureFormKind(kind) ? `/w/${slug}/app/setup?connect=${encodeURIComponent(kind)}#manage-sources` : onboardingHref(slug, 'connect');
}

const kindOf = (e: EnablerItem) => e.progress.kind ?? (e.code.startsWith('connect:') ? e.code.slice(8) : null);
const when = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const day = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

export function EnablerList({ slug, enablers, updatedAt, people, viewer, canChange, program, onChanged, cliVersion, settled = true, link, navKey = '', onShowAll }: {
  slug: string; enablers: readonly EnablerItem[]; updatedAt: number; people: readonly Person[]; viewer: ListViewer; canChange: boolean;
  program: string | null; onChanged: () => Promise<unknown>; cliVersion: string | null;
  /**
   * V109-1-19 R3 (Medium "Setup cache"): false while the list shown is a cached copy and a refetch is on
   * its way — a link to a step is not declared gone until the fresh list has landed.
   */
  settled?: boolean;
  /** V108-8-20: the link's step / fix (the router's search params); absent → read from the address bar. */
  link?: { step: string | null; enabler: string | null; subject: string | null } | null;
  /**
   * V108-8-20: changes on EVERY navigation (the router's `location.key`), so following a link to a step opens, scrolls
   * to and focuses it again even when the address doesn't change (the user already came from that link).
   */
  navKey?: string;
  /** V108-8-20: the linked step is hidden by the program filter: show every step so it can open. */
  onShowAll?: () => void;
}) {
  const approver = viewer.role === 'owner' || viewer.role === 'admin';
  // Check now announces the REFRESHED state (build review a11y H3), so rows read the latest list through a ref.
  const latest = useRef(enablers);
  latest.current = enablers;
  const { groups } = useMemo(() => groupSteps(enablers, program), [enablers, program]);
  const [toastNode, toast] = useSetupToast();
  const [announceNode, announce] = useAnnouncer();
  // V109-1-19 (B1): a "Couldn't check" fix links here by enabler code AND subject; it opens exactly
  // that step. No silent fallback to another step: an unmatched link says the fix isn't open any more.
  // R3 (Medium): recomputed whenever the list refetches, so a cached list never decides it.
  const linkStep = link ? link.step : new URLSearchParams(window.location.search).get('step');
  const linkCode = link ? link.enabler : new URLSearchParams(window.location.search).get('enabler');
  const linkSubject = link ? link.subject : new URLSearchParams(window.location.search).get('subject');
  const linked = useMemo(() => {
    const step = linkStep;
    if (step && enablers.some((e) => e.id === step)) return { id: step, gone: null as string | null, subject: null as string | null, stale: null as string | null };
    const code = linkCode;
    // V108-8-20: a link to a step that isn't on the list (stale) says so in place, never silently nothing.
    if (!code) return step ? { id: null, gone: null, subject: null, stale: settled ? step : null } : null;
    const subject = linkSubject;
    const match = (e: EnablerItem) => e.code === code && (!subject || e.subject_key === subject);
    const hit = enablers.find((e) => match(e) && e.state !== 'done') ?? enablers.find(match);
    return hit ? { id: hit.id, gone: null, subject, stale: null } : { id: null, gone: settled ? code : null, subject, stale: null };
  }, [enablers, settled, linkStep, linkCode, linkSubject]);
  const initialOpen = () => {
    if (linked) return linked.id; // a link opens its step, or nothing (never another step)
    const flight = [...groups.progress].find((e) => e.progress.state === 'waiting' || e.progress.state === 'failed' || e.progress.state === 'needs_fix' || e.progress.state === 'not_counted' || !!e.progress.ci_trust);
    return flight?.id ?? groups.open.find((e) => e.state !== 'blocked')?.id ?? null;
  };
  const [openId, setOpenId] = useState<string | null>(initialOpen);
  const rowRefs = useRef(new Map<string, HTMLLIElement>());
  const focusRow = (id: string | undefined, part: 'title' | 'toggle' = 'title') => window.setTimeout(() => {
    const row = id ? rowRefs.current.get(id) : null;
    (row?.querySelector<HTMLElement>(part === 'title' ? '.su-row-title' : '[data-part="toggle"]') ?? row?.querySelector<HTMLElement>('button, a'))?.focus();
  }, 0);
  // V109-1-19 (H5): arriving from a link, the list has loaded by now — focus and scroll to the opened row.
  // R3: once per arrival, on the first list that resolves the link (the fresh one when the cache was stale).
  const goneRef = useRef<HTMLDivElement>(null);
  const arrived = useRef<string | null>(null);
  // V108-8-20 (aesthetic High): the stale-link notice shows on arrival and goes away once its way forward is used.
  const [staleShown, setStaleShown] = useState<string | null>(null);
  // V108-8-20: once per NAVIGATION (navKey), not once per address: following the same link again re-opens the row.
  // A row hidden by the program filter shows every step first (onShowAll), then opens once it is rendered.
  const hiddenByFilter = !!linked?.id && !!program && !GROUP_ORDER.some((g) => groups[g].some((e) => e.id === linked.id));
  useEffect(() => {
    if (!linked) return;
    const target = linked.id ?? (linked.gone ? `gone:${linked.gone}` : linked.stale ? `stale:${linked.stale}` : null);
    if (!target) return;
    if (hiddenByFilter && onShowAll) {
      onShowAll();
      announce('Showing all steps, so the linked step is in the list.'); // a11y review (Medium): the view changed
      return;
    }
    const key = `${target}|${navKey}`;
    if (arrived.current === key) return;
    arrived.current = key;
    if (linked.stale) setStaleShown(linked.stale);
    if (linked.id) {
      setOpenId(linked.id);
      window.setTimeout(() => rowRefs.current.get(linked.id!)?.scrollIntoView?.({ block: 'start' }), 0);
      focusRow(linked.id);
    } else window.setTimeout(() => { goneRef.current?.scrollIntoView?.({ block: 'start' }); goneRef.current?.focus(); }, 0);
  }, [linked, navKey, hiddenByFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const flat = GROUP_ORDER.flatMap((g) => groups[g]);
  const firstOpen = flat.find((e) => groupOf(e) === 'open' || groupOf(e) === 'progress') ?? null;
  // V108-8-8: the "why it moves nothing" line once per kind of evidence (first such row); later rows get the chip.
  const fullWhy = new Set<string>();
  const whySeen = new Set<string>();
  for (const e of flat) if (movesNothing(e) && !whySeen.has(evidenceNoun(e.code))) { whySeen.add(evidenceNoun(e.code)); fullWhy.add(e.id); }
  const nextAfter = (id: string) => { const i = flat.findIndex((e) => e.id === id); return flat[i + 1]?.id ?? flat[i - 1]?.id; };

  const patch = async (e: EnablerItem, body: EnablerPatch, msg: string, undo?: EnablerPatch, focusId?: string) => {
    await patchEnabler(slug, e.id, body);
    await onChanged();
    toast(msg, undo ? { onUndo: () => { void patchEnabler(slug, e.id, undo).then(onChanged).then(() => focusRow(e.id)); } } : undefined);
    if (focusId) focusRow(focusId, 'toggle');
  };

  return (
    <div className="su-steps" data-part="step-groups">
      {announceNode}
      {linked?.gone ? <FixGoneNotice slug={slug} code={linked.gone} subject={linked.subject} approver={approver} refEl={goneRef} announce={announce} /> : null}
      {/* V108-8-20: a stale step link says so (focus moves here, so no live region), with a way forward; it goes away once used. */}
      {linked?.stale && staleShown !== linked.stale ? null : linked?.stale ? <div className="su-inline-form" role="group" aria-labelledby="su-stale-msg" tabIndex={-1} ref={goneRef} data-part="step-gone">
        <p className="su-gone-line" id="su-stale-msg" style={{ margin: 0 }}>This step is no longer on the list. GAC Pro doesn't need it any more.</p>
        <div className="su-acts">{firstOpen ? <Button compact onClick={() => { setStaleShown(null); setOpenId(firstOpen.id); window.setTimeout(() => rowRefs.current.get(firstOpen.id)?.scrollIntoView?.({ block: 'start' }), 0); focusRow(firstOpen.id); }}>
          Go to the first open step</Button> : <a className="su-linkish" href="#manage-sources" onClick={() => setStaleShown(null)}>Manage sources</a>}</div>
      </div> : null}
      {GROUP_ORDER.map((g) => groups[g].length ? (
        <section key={g} aria-labelledby={`su-g-${g}`} data-group={g} style={{ display: 'grid', gap: 'var(--sp-3)' }}>
          <h3 className="su-group-h" id={`su-g-${g}`} tabIndex={-1}>{g === 'attention' ? <span className="su-attn-mark" aria-hidden="true">!</span> : null}{GROUP_LABEL[g]} <span className="su-n">{groups[g].length}</span>
</h3>
          <ul className="su-list" aria-labelledby={`su-g-${g}`}>
            {groups[g].map((e) => (
              <StepRow key={e.id} e={e} group={g} slug={slug} people={people} viewer={viewer} approver={approver} canChange={canChange}
                open={openId === e.id} onToggle={() => setOpenId((o) => (o === e.id ? null : e.id))} updatedAt={updatedAt} cliVersion={cliVersion}
                rowRef={(el) => { if (el) rowRefs.current.set(e.id, el); else rowRefs.current.delete(e.id); }}
                goTo={(id) => { setOpenId(id); focusRow(id, 'toggle'); rowRefs.current.get(id)?.scrollIntoView?.({ block: 'start' }); }}
                onPatch={(body, msg, undo, moveFocus) => patch(e, body, msg, undo, moveFocus ? nextAfter(e.id) : undefined)}
                onChanged={onChanged} announce={announce} toast={toast} enablers={enablers} latest={() => latest.current}
                why={movesNothing(e) ? (fullWhy.has(e.id) ? 'line' : 'chip') : null} />
            ))}
          </ul>
        </section>
      ) : null)}
      {toastNode}
    </div>
  );
}

/**
 * V109-1-19 (B1): a fix link whose step isn't open (the subject is no longer computed, or it was done) —
 * say so, never open another step. R3 (R2-H1 / Medium): it never claims the fix "was verified" (GAC Pro
 * only knows nothing is open for it); one line naming the source; and when the subject is a live
 * connection, "Check now" reads it again right here (owner / admin).
 */
export function FixGoneNotice({ slug, code, subject = null, approver = false, refEl, announce }: {
  slug: string; code: string; subject?: string | null; approver?: boolean; refEl?: React.Ref<HTMLDivElement>; announce?: (m: string) => void;
}) {
  const entry = getEnablerCatalogEntry(code);
  const connectorId = subject?.startsWith('connector:') ? subject.slice('connector:'.length) : null;
  const conns = useQuery({ queryKey: queryKeys.postureConnectors(slug), queryFn: () => listPostureConnectors(slug), enabled: !!connectorId, retry: false, staleTime: 30_000 });
  const conn = connectorId ? conns.data?.connectors.find((c) => c.connector_id === connectorId) ?? null : null;
  const source = conn ? vendorName(conn.kind) : null;
  const [checking, setChecking] = useState(false);
  const live = !!conn && conn.status === 'active';
  const checkNow = async () => {
    if (!connectorId || checking) return;
    setChecking(true);
    try { await runConnectorCheck(slug, connectorId); announce?.(`Checking ${source ?? 'the connection'} now. Anything it can't read shows here as a step.`); }
    catch (err) { announce?.(err instanceof Error ? err.message : 'The check could not start. Try again.'); }
    finally { setChecking(false); }
  };
  return (
    <div className="su-inline-form" role="status" tabIndex={-1} ref={refEl} data-part="fix-gone">
      <p className="su-row-title su-gone-line">{`GAC Pro has nothing open for this fix${source ? ` on ${source}` : ''}.`}
        {live && approver ? <Button compact onClick={() => void checkNow()} aria-disabled={checking || undefined}>{checking ? 'Checking…' : 'Check now'}<span className="su-sr">{` ${source ?? ''}`}</span></Button> : null}</p>
      {entry?.steps.length ? <details className="su-more"><summary>What the fix was</summary><ol>{entry.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></details> : null}
    </div>
  );
}

const isFix = (e: EnablerItem) => e.code.startsWith('fix:');

/**
 * V109-1-19 R3 (Low "Setup step copy"): rendered IN Setup, a step that only says to go to Setup is noise —
 * the action is right below it.
 */
export function inSetup(steps: readonly string[]): string[] {
  return steps.filter((s) => s !== OPEN_SETUP_STEP && !/^In GAC Pro, open Setup( and (choose|find) [^.]+)?\.$/.test(s));
}

interface RowProps {
  e: EnablerItem; group: StepGroup; slug: string; people: readonly Person[]; viewer: ListViewer; approver: boolean; canChange: boolean; open: boolean;
  onToggle: () => void; updatedAt: number; cliVersion: string | null; rowRef: (el: HTMLLIElement | null) => void; goTo: (id: string) => void;
  onPatch: (body: EnablerPatch, msg: string, undo?: EnablerPatch, moveFocus?: boolean) => Promise<void>;
  onChanged: () => Promise<unknown>; announce: (m: string) => void; toast: (m: string, o?: { onUndo?: () => void }) => void; enablers: readonly EnablerItem[];
  latest: () => readonly EnablerItem[];
  /** V108-8-8: this step moves no objective: the full line (first such row) or the short chip. */
  why?: 'line' | 'chip' | null;
}

function StepRow(p: RowProps) {
  const { e, group, slug, viewer, approver } = p;
  const parked = group === 'done' || group === 'attention' || group === 'snoozed' || group === 'declined';
  const pr = e.progress;
  const cause = causeOf(e, slug);
  const who = personLabel(e.assignee_user_id, viewer.user_id, p.people);
  const kind = kindOf(e);
  const [checking, setChecking] = useState(false);
  const bodyId = `su-body-${e.id}`;
  // V108-8-20: a done CI gate step receiving new signed runs that don't count: Done stays, the attention shows too.
  const newRuns = isNewCiRun(e);

  const checkNow = async () => {
    if (!pr.connector_id || checking) return;
    setChecking(true);
    try {
      await runConnectorCheck(slug, pr.connector_id);
      await p.onChanged();
      await new Promise((r) => window.setTimeout(r, 0));
      p.announce(checkResult(p.latest().find((x) => x.id === e.id), slug));
    } catch (err) {
      p.announce(err instanceof Error ? err.message : 'The check could not start. Try again.');
    } finally { setChecking(false); }
  };

  const meta = parked ? null : (
    <div className="su-row-meta">
      <UnlockChips step={e} />
      {p.why === 'chip' && <NotTakenChip />}
      {e.privilege.level === 'beyond_read_only' && <span className="su-chip" data-kind="privilege"><Glyph kind="alert" />Beyond read-only</span>}
      {pr.environment && pr.environment !== 'production' && pr.state !== 'not_started' && <span className="su-chip" data-kind="preview"><Glyph kind="eye" />Preview · doesn't count</span>}
      {/* V108-8-18 (c): right where the preview shows, say whether it is production (owner / admin; others ask). */}
      {pr.environment && pr.environment !== 'production' && pr.state !== 'not_started' && pr.connector_id && <EnvironmentChoice slug={slug} connectorId={pr.connector_id} kind={pr.kind}
        environment={pr.environment} target={pr.target_label} approver={approver} onChanged={p.onChanged} announce={p.announce} />}
      {/* Aesthetic M (V108-8-17 review): adding a system to a program is done here, not in a vendor console. */}
      {pr.gap?.kind === 'system_scope' || pr.gap?.kind === 'system_classify' ? <span className="su-kind">{`In GAC Pro · ${e.effort_min} min`}</span> : <ActorKind step={e} />}
      <span className="su-who" data-none={who ? undefined : ''}>{who ?? 'Unassigned'}{e.assignee_user_id && e.assigned_at ? ` · sent ${day(e.assigned_at)}` : ''}</span>
    </div>
  );

  return (
    <li ref={p.rowRef} className={`su-row${parked ? ' su-parked' : ''}`} id={`step-${e.id}`} data-open={p.open ? '' : undefined} data-step={e.code} data-state={pr.state}
      data-attention={newRuns ? '' : undefined}>
      <div className="su-row-head">
        <div>
          <h4 className="su-row-title" tabIndex={-1}>{e.title}{newRuns ? <span className="su-sr"> · needs attention</span> : null}</h4>
          {/* V108-8-20: the words people use for this step ("CI gate"), and the Manage sources section's name. */}
          {e.code.startsWith('ci-gate:') && <p className="su-row-alias" data-part="ci-gate-alias">CI governance check (CI gate)</p>}
          {meta}
          {parked ? <ParkedLine e={e} people={p.people} viewer={viewer} /> : <StatusLine e={e} cause={cause} updatedAt={p.updatedAt} enablers={p.enablers} goTo={p.goTo} />}
          {!parked && p.why === 'line' && <NotTakenLine step={e} prefix={`/w/${slug}/app`} />}
        </div>
        <div className="su-row-actions">
          {!parked && approver && pr.connector_id && (pr.state === 'waiting' || pr.state === 'failed') && (
            <Button compact onClick={() => void checkNow()} aria-disabled={checking || undefined}>{checking ? 'Checking…' : 'Check now'}<span className="su-sr"> for {e.title}</span></Button>
          )}
          {(group === 'done' || group === 'attention') && pr.connector_id && <Link className="su-btn" data-variant="ghost" to={`/w/${slug}/app/setup#posture-${pr.connector_id}`}>View connection<span className="su-sr">: {e.title}</span></Link>}
          {group === 'snoozed' && p.canChange && <Button variant="ghost" compact onClick={() => void p.onPatch({ state: 'reopen' }, 'Back in Open.')}>Bring back now<span className="su-sr">: {e.title}</span></Button>}
          {group === 'declined' && p.canChange && <Button variant="ghost" compact onClick={() => void p.onPatch({ state: 'reopen' }, 'Back in Open.')}>Reopen<span className="su-sr">: {e.title}</span></Button>}
          {!parked && (
            <Button compact data-part="toggle" aria-expanded={p.open} aria-controls={bodyId} onClick={p.onToggle}>
              {p.open ? 'Hide steps' : 'Show steps'}<span className="su-sr"> for {e.title}</span>
            </Button>
          )}
        </div>
      </div>
      {!parked && <div className="su-row-body" id={bodyId} hidden={!p.open}>{p.open && <RowBody {...p} cause={cause} checkNow={checkNow} />}</div>}
      {newRuns && pr.ci_trust && <div className="su-row-body" data-part="ci-trust-attention">
        <p className="su-row-sub" data-part="ci-trust" style={{ marginTop: 0 }}><CiTrustState t={pr.ci_trust} /><span className="su-sr">: </span><strong>{ciTrustLine(pr.ci_trust)}</strong></p>
        <p className="su-note">Runs that were trusted before still count.</p>
        {pr.ci_trust.status !== 'not_counted'
          ? <div className="su-acts"><CiTrustAction slug={slug} step={e} approver={approver} all viewerId={viewer.user_id} onChanged={p.onChanged} announce={p.announce} /></div>
          : pr.ci_trust.unsigned ? <SigningHint />
            : <div className="su-acts"><Link className="su-btn" to={`/w/${encodeURIComponent(slug)}/app/settings/security#ci-identities`}>Open CI identities</Link></div>}
      </div>}
      {/* V108-8-17 / V108-8-18: a setup gap's actions sit on the row itself, open or not. */}
      {!parked && pr.gap && <div className="su-row-body" data-part="gap"><GapActions gap={pr.gap} slug={slug} viewerId={viewer.user_id} approver={approver} title={e.title}
        onChanged={p.onChanged} announce={p.announce} /></div>}
      {/* AC-3: "add the other N" sits on the row itself, open or not. */}
      {pr.state === 'partial' && kind === 'aws' && <div className="su-row-body" data-part="accounts"><AwsAccounts {...p} /></div>}
    </li>
  );
}

function ParkedLine({ e, people, viewer }: { e: EnablerItem; people: readonly Person[]; viewer: ListViewer }) {
  if (e.state === 'done') {
    return <p className="su-row-sub"><State tone="done" glyph="check">Done</State>
      <span>{[e.progress.target_label, e.progress.first_read_at || e.verified_at ? `first read ${when((e.progress.first_read_at ?? e.verified_at)!)}` : null].filter(Boolean).join(' · ')}</span></p>;
  }
  const by = personLabel(e.assigned_by ?? null, viewer.user_id, people);
  const parts = [e.state === 'snoozed' && e.snoozed_until ? `Snoozed until ${day(e.snoozed_until)}` : null, e.reason, by ? `${by}, ${day(e.updated_at)}` : day(e.updated_at)].filter(Boolean);
  return <div className="su-row-meta">{parts.map((t, i) => <span key={i}>{i ? <span aria-hidden="true">· </span> : null}{t}</span>)}</div>;
}

/** The live line under the title. V108-8-21: no client-clock stamp here; the page has one "Checked N min ago" line. */
function StatusLine({ e, cause, enablers, goTo }: { e: EnablerItem; cause: ReturnType<typeof causeOf>; updatedAt?: number; enablers: readonly EnablerItem[]; goTo: (id: string) => void }) {
  const pr = e.progress;
  // V108-8-13: a CI gate step whose runs arrive but don't count yet (or that was just trusted) says why first.
  if (pr.ci_trust && e.state !== 'done') {
    return <p className="su-row-sub" data-part="ci-trust"><CiTrustState t={pr.ci_trust} /><strong>{ciTrustLine(pr.ci_trust)}</strong></p>;
  }
  if (e.state === 'blocked') {
    const prereq = (e.reason ?? '').replace(/^Waiting on /, '').split(',').map((s) => s.trim()).filter(Boolean);
    const target = enablers.find((x) => prereq.includes(x.code));
    return <p className="su-row-sub"><State tone="muted" glyph="lock">Blocked</State>
      <span>· unblocks when {target ? <strong>{target.title}</strong> : 'another step'} is done</span>
      {target && <button type="button" className="su-linkish" onClick={() => goTo(target.id)}>Go to {target.title}</button>}</p>;
  }
  // V109-1-19 (B3): a "Couldn't check" fix is never first-time setup — it names what still can't be read.
  // R3 (R2-H6): the same grey pill as readiness (never amber; amber is "Needs attention"), the shared reason
  // words first and the provider's cause only when no reason is known; "N resources", never "affected".
  if (isFix(e) && (pr.state === 'needs_fix' || pr.state === 'waiting' || pr.state === 'not_started' || pr.state === 'failed')) {
    const words = [...new Set((pr.fix?.reasons ?? []).filter(isCouldntCheckReason).map((r) => couldntCheckEntry(r).words))];
    const n = (pr.fix?.resources.length ?? 0) + (pr.fix?.more ?? 0);
    const why = words.length ? words.join(' · ') : cause?.line ?? 'needs this fix before GAC Pro can check it';
    return <p className="su-row-sub" data-part="needs-fix"><CouldntCheckChip />
      <span>{`· ${why}`}{n ? ` · ${n} resource${n === 1 ? '' : 's'}` : ''}</span></p>;
  }
  if (pr.state === 'waiting' && (!pr.needs_console || pr.consent_started_at)) {
    return <p className="su-row-sub"><State tone="progress" glyph="clock">Waiting for our first read</State>
</p>;
  }
  if (pr.state === 'failed' && cause) {
    return <p className="su-row-sub"><State tone="attention" glyph="alert">Needs attention</State><strong>{cause.line}</strong>
</p>;
  }
  if (pr.state === 'partial' && pr.accounts) {
    const n = pr.accounts.read.length + pr.accounts.unread.length;
    return <p className="su-row-sub"><State tone="partial" glyph="clock">{pr.accounts.read.length} of {n} accounts read</State>
      <span>· nothing counts until every production account is read</span></p>;
  }
  if (e.state === 'in_progress' && pr.state === 'not_started') return <p className="su-row-sub"><State tone="progress" glyph="clock">In progress</State><span>· shows as done after our first read</span></p>;
  return null;
}

function RowBody(p: RowProps & { cause: ReturnType<typeof causeOf>; checkNow: () => Promise<void> }) {
  const { e, slug, approver, cause } = p;
  const pr = e.progress;
  const kind = kindOf(e);
  const [slot, setSlot] = useState<'send' | 'skip' | null>(null);
  const sendBtn = useRef<HTMLButtonElement>(null);
  const skipBtn = useRef<HTMLButtonElement>(null);
  const consent = async (started: boolean) => {
    if (!pr.connector_id) return;
    try { await setConsentStarted(slug, pr.connector_id, started); await p.onChanged(); if (!started) p.toast('Back to not started.'); }
    catch (err) { p.announce(err instanceof Error ? err.message : 'That did not save. Try again.'); }
  };
  const vendor = vendorName(kind ?? '');
  const consoleUrl = kind ? CONSOLE_URL[kind] : undefined;

  let main: ReactNode = null;
  const fixAction = fixActionFor(e, slug);
  const [turning, setTurning] = useState(false);
  const turnOn = async (connectorId: string) => {
    if (turning) return;
    setTurning(true);
    try { await setPostureConnectorStatus(slug, connectorId, 'active'); await p.onChanged(); p.announce(`${e.title.replace(/^Turn (.+) back on$/, '$1')} is on again. GAC Pro collects from it now.`); }
    catch (err) { p.announce(err instanceof Error ? err.message : 'That did not turn it on. Try again.'); }
    finally { setTurning(false); }
  };
  if (pr.ci_trust && e.state !== 'done' && pr.ci_trust.status !== 'not_counted') {
    // V108-8-13: ONE action: Trust it (owner / admin; anyone else asks an admin), Open pull requests, or Run it on GitHub.
    main = <div style={{ display: 'grid', gap: 'var(--sp-2)' }} data-part="ci-trust-body">
      <div className="su-acts"><CiTrustAction slug={slug} step={e} approver={approver} primary all viewerId={p.viewer.user_id} onChanged={p.onChanged} announce={p.announce} /></div>
      {pr.ci_trust.status === 'trusted' && <p className="su-note">Shows as done when that run arrives.</p>}
    </div>;
  } else if (isFix(e) && fixAction && pr.state !== 'failed' && pr.state !== 'done') {
    // V109-1-19 (B2/B3): every fix row has its steps, what it affects and ONE in-app action. No
    // "first read" text, and no PR / CLI note when there is no command (the steps say what to do).
    const res = pr.fix?.resources ?? [];
    const n = res.length + (pr.fix?.more ?? 0);
    const api = pr.fix?.api ?? null;
    main = <div style={{ display: 'grid', gap: 'var(--sp-2)' }} data-part="fix-body">
      {e.instructions.command ? <CodeBlock label={`${e.title} command`} code={e.instructions.command} /> : null}
      {/* R3 (Medium "enable-api"): the API that is off, by name. */}
      {api?.name ? <p className="su-note" data-part="fix-api">{`The API that is off: ${api.name}${api.service ? ` (${api.service})` : ''}.`}</p> : null}
      {inSetup(e.instructions.steps).length > 0 && <ol className="su-steps-inline">{inSetup(e.instructions.steps).map((s, i) => <li key={i}>{s}</li>)}</ol>}
      {res.length ? <div><p className="su-note">{`Couldn't check ${n} resource${n === 1 ? '' : 's'}:`}</p><ul className="su-acct-list" data-part="fix-resources">{res.map((r) => <li key={r}>{r}</li>)}
        {pr.fix?.more ? <li>{`and ${pr.fix.more} more`}</li> : null}</ul></div> : null}
      {fixAction.kind === 'snippet' ? <div className="su-snippet" data-part="fix-snippet"><CodeBlock label="CI example: keep the measured output" code={fixAction.snippet} /></div> : null}
      <div className="su-acts">
        {fixAction.kind === 'collect'
          ? (approver ? <Button compact onClick={() => void p.checkNow()}>{fixAction.label}<span className="su-sr"> for {e.title}</span></Button>
            : <span className="su-hint">GAC Pro collects again on its own at the next collection.</span>)
          : fixAction.kind === 'turn-on'
            ? (approver ? <Button compact onClick={() => void turnOn(fixAction.connectorId)} aria-disabled={turning || undefined} data-part="fix-action">{turning ? 'Turning on…' : fixAction.label}<span className="su-sr">: {e.title}</span></Button>
              : <span className="su-hint">An owner or admin turns it back on.</span>)
            : fixAction.kind === 'external'
              ? <ExtLink href={fixAction.href} variant="primary">{fixAction.label}</ExtLink>
              : fixAction.kind === 'snippet'
                ? <Link className="su-btn" to={fixAction.href} data-part="fix-action">Open the system</Link>
                : <Link className="su-btn" data-variant="primary" to={fixAction.href} data-part="fix-action">{fixAction.label}</Link>}
      </div>
      <p className="su-note">{e.code === 'fix:turn-on' ? 'Shows as done when the connection is on.' : "Shows as done when a collection can check it. You can't mark it done yourself."}</p>
    </div>;
  } else if (pr.state === 'failed' && cause) {
    // The cause line is already on the row; the body says only the fix (no repeat, no live role: AC-6 / a11y H2).
    main = <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
      <p className="su-note" style={{ color: 'var(--text)', fontSize: 'var(--fs-body)' }}>{cause.fix}</p>
      {cause.paste && <CodeBlock label={cause.paste.label} code={cause.paste.value} />}
      {pr.cause?.detail && <details className="su-more"><summary>Details from {vendor}</summary><p className="su-note su-mono">{pr.cause.detail}</p></details>}
      <div className="su-acts">
        {approver ? (cause.action.external ? <ExtLink href={cause.action.href} variant="primary">{cause.action.label}</ExtLink>
          : <Link className="su-btn" data-variant="primary" to={cause.action.href}>{cause.action.label}</Link>) : <InFlightWith {...p} />}
        {approver && pr.consent_started_at && <Button variant="ghost" compact onClick={() => void consent(false)}>I didn't finish</Button>}
      </div>
    </div>;
  } else if (e.actor === 'admin_console' && !approver && e.assignee_user_id && e.assignee_user_id !== p.viewer.user_id) {
    // AC-4 on Setup: state before role — someone is on it; Remind, never a second ask or a console link.
    main = <InFlightWith {...p} />;
  } else if (e.actor === 'admin_console') {
    if (pr.state === 'waiting' && pr.needs_console && pr.consent_started_at) {
      main = <div className="su-acts">
        {consoleUrl && approver && <ExtLink href={consoleUrl}>Open {vendor} again to finish approving</ExtLink>}
        {approver && <Button variant="ghost" compact onClick={() => void consent(false)}>I didn't finish</Button>}
      </div>;
    } else if (pr.state === 'waiting' && pr.needs_console && approver) {
      main = <>
        <ol className="su-steps-inline">
          <li>Sign in to the production {vendor} account as an admin.</li>
          <li>Create the read-only role from the template on the connection; paste the external ID shown there.</li>
        </ol>
        <div className="su-acts">
          {consoleUrl && <ExtLink href={consoleUrl} variant="primary" onClick={() => void consent(true)}>Open {vendor} to approve</ExtLink>}
          {pr.connector_id && <Link className="su-linkish" to={`/w/${slug}/app/setup#posture-${pr.connector_id}`}>See the role template</Link>}
        </div>
      </>;
    } else if (pr.state === 'waiting') {
      main = <p className="su-note">Shows as done after our first read{pr.target_label ? ` from ${pr.target_label}` : ''}.</p>;
    } else if (pr.state === 'not_started' && kind) {
      main = <>
        {inSetup(e.instructions.steps).length > 0 && <ol className="su-steps-inline">{inSetup(e.instructions.steps).map((s, i) => <li key={i}>{s}</li>)}</ol>}
        {approver ? <div className="su-acts"><Link className="su-btn" data-variant="primary" to={connectHref(slug, kind)}>{e.title}</Link></div>
          : <p className="su-hint">Connecting needs an owner or admin of this workspace.{p.canChange ? ' Send it to one below.' : ''}</p>}
        <p className="su-note">Shows as done after our first read. You can't mark it done yourself.</p>
      </>;
    }
    if (kind === 'aws' && pr.state === 'not_started' && approver) main = <>{main}<AwsAccounts {...p} /></>;
  } else {
    main = <>
      {/* V108-8-20: unsigned results are fixed by signing, not by trust. */}
      {pr.ci_trust?.status === 'not_counted' && pr.ci_trust.unsigned ? <SigningHint /> : null}
      {e.instructions.command && <CodeBlock label={`${e.title} command`} code={e.instructions.command} />}
      {!e.instructions.version_pinned && <p className="su-note">Runs the latest GAC Pro CLI (no published version is pinned yet).</p>}
      <p className="su-note">{e.actor === 'repo_agent' ? 'Opens one pull request; nothing changes until you merge. Shows as done after the first run on your main branch.' : `Shows as done after the collector's first report.`}</p>
      {e.actor === 'repo_agent' && <details className="su-more"><summary>What the pull request does</summary><ol>
        <li>Adds one change on a new branch{e.instructions.secret_names.length ? `, reading the secret ${e.instructions.secret_names.join(', ')}` : ''}.</li>
        <li>Reports only; doesn't block merges unless you make the check required.</li>
        <li>Nothing is applied until someone on your team merges it.</li>
      </ol></details>}
      {e.instructions.steps.length > 0 && <details className="su-more"><summary>Do it by hand</summary><ol>{e.instructions.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></details>}
    </>;
  }

  return <>
    {main}
    <TrustDisclosure e={e} kind={kind} />
    {!p.canChange && !approver && !e.assignee_user_id && (
      <div className="su-acts" data-minor=""><Button variant="ghost" compact onClick={() => void navigator.clipboard?.writeText(new URL(`/w/${slug}/app/setup?step=${e.id}#steps`, window.location.origin).toString()).then(() => p.toast('Link copied. Send it to an owner or admin.'), () => p.toast('Copy the address bar link and send it to an owner or admin.'))}>Copy a link for an admin</Button></div>
    )}
    {p.canChange && (
      <div className="su-acts" data-minor="">
        {p.canChange && <Button ref={sendBtn} variant="ghost" compact aria-expanded={slot === 'send'} onClick={() => setSlot(slot === 'send' ? null : 'send')}>{e.assignee_user_id ? 'Change who' : 'Send to someone'}</Button>}
        {p.canChange && <Button ref={skipBtn} variant="ghost" compact aria-expanded={slot === 'skip'} onClick={() => setSlot(slot === 'skip' ? null : 'skip')}>Not doing this</Button>}
      </div>
    )}
    {slot === 'send' && <SendForm {...p} onDone={() => { setSlot(null); sendBtn.current?.focus(); }} />}
    {slot === 'skip' && <SkipForm {...p} onCancel={() => { setSlot(null); skipBtn.current?.focus(); }} />}
  </>;
}

/** A step someone else holds: who has it, and Remind (copies a reminder to paste; no ask tracking yet). */
function InFlightWith(p: RowProps) {
  const owner = personLabel(p.e.assignee_user_id, p.viewer.user_id, p.people);
  if (!owner) return <p className="su-hint">An owner or admin fixes this.{p.canChange ? ' Send it to one below.' : ''}</p>;
  const remind = async () => {
    const url = new URL(`/w/${p.slug}/app/setup?step=${p.e.id}#steps`, window.location.origin).toString();
    try { await navigator.clipboard.writeText(`Reminder: "${p.e.title}" in GAC Pro is waiting on you. ${url}`); p.toast(`Copied a reminder for ${owner}. Paste it in chat or email.`); }
    catch { p.toast(`Couldn't copy. Send ${owner} this link: ${url}`); }
  };
  return <div className="su-acts"><span className="su-hint">With {owner}.</span><Button compact onClick={() => void remind()}>{`Remind ${owner}`}</Button></div>;
}

/** Trust disclosures, collapsed (design: see exactly what the role can do / how we hold this key). */
function TrustDisclosure({ e, kind }: { e: EnablerItem; kind: string | null }) {
  if (kind === 'aws') {
    const xid = e.progress.fix_context?.external_id;
    return <details className="su-more"><summary>See exactly what the role can do</summary><dl className="su-kv">
      <dt>Permissions</dt><dd>AWS managed policies <span className="su-mono">SecurityAudit</span>, <span className="su-mono">AmazonBedrockReadOnly</span> and <span className="su-mono">AmazonSageMakerReadOnly</span>. No write actions.</dd>
      <dt>Can't read</dt><dd>S3 object contents, database rows or secret values. An explicit deny blocks reading agent runtime secrets.</dd>
      <dt>Who can use it</dt><dd>GAC Pro's AWS identity, and only with your external ID.</dd>
      {xid && <><dt>External ID</dt><dd className="su-mono">{xid}</dd></>}
      <dt>To remove it</dt><dd>Delete the role in IAM. Reads stop at once.</dd>
    </dl></details>;
  }
  if (e.privilege.level === 'beyond_read_only') {
    return <>
      <p className="su-warn" data-tone="privilege"><Glyph kind="alert" /><span>{e.privilege.reason} GAC Pro only reads with it.</span></p>
      <details className="su-more"><summary>How we hold this key</summary><ul>
        <li>Encrypted as soon as you save it. Nobody, including you, can see it again.</li>
        <li>We only call read endpoints.</li>
        <li>Revoke it any time in the vendor's console, or remove the connection here.</li>
      </ul></details>
    </>;
  }
  return null;
}

/** AC-3: one account or several in an AWS Organization; the declared accounts and which are read. */
function AwsAccounts(p: RowProps) {
  const { e, slug, approver } = p;
  const accts = e.progress.accounts;
  const [mode, setMode] = useState<'one' | 'several' | null>(accts ? 'several' : null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const save = async (ev: FormEvent) => {
    ev.preventDefault();
    const rows = text.split(/\n+/).map((l) => l.trim()).filter(Boolean).map((l) => { const [id, ...rest] = l.split(/[\s,]+/); return { account_id: id ?? '', label: rest.join(' ') || null }; });
    if (!rows.length || rows.some((r) => !/^\d{4}-?\d{4}-?\d{4}$/.test(r.account_id))) { setError('Enter 12-digit AWS account IDs, one per line, with an optional name after each.'); return; }
    try { await declareAwsAccounts(slug, rows); setError(''); await p.onChanged(); p.toast(`Saved ${rows.length} production accounts.`); }
    catch (err) { setError(err instanceof Error ? err.message : 'That did not save. Try again.'); }
  };
  if (accts && accts.unread.length) {
    return <details className="su-more" open><summary>Reading {accts.read.length} of {accts.read.length + accts.unread.length} production accounts · add the other {accts.unread.length}</summary>
      <ul className="su-acct-list">{accts.unread.map((a) => (
        <li key={a.account_id}><span><span className="su-mono">{formatAwsAccount(a.account_id)}</span>{a.label ? ` · ${a.label}` : ''}</span>
          {approver && <span className="su-acts">
            <Link className="su-btn" data-variant="ghost" to={connectHref(slug, 'aws')}>Connect this account<span className="su-sr"> {a.account_id}</span></Link>
            <Button variant="ghost" compact onClick={() => void setAwsAccountState(slug, a.account_id, 'not_production').then(p.onChanged).then(() => p.toast(`${formatAwsAccount(a.account_id)} marked not production.`))}>Not production<span className="su-sr">: {a.account_id}</span></Button>
          </span>}
        </li>))}</ul>
    </details>;
  }
  if (!approver) return null;
  return <form className="su-inline-form" onSubmit={save} noValidate aria-label="Production accounts">
    <fieldset>
      <legend>Which accounts run production?</legend>
      <label className="su-radio"><input type="radio" name={`acct-${e.id}`} checked={mode === 'one'} onChange={() => setMode('one')} />One account</label>
      <label className="su-radio"><input type="radio" name={`acct-${e.id}`} checked={mode === 'several'} onChange={() => setMode('several')} />Several, in an AWS Organization</label>
    </fieldset>
    {mode === 'several' && <>
      <Field id={`acct-ids-${e.id}`} label="Production account IDs" hint="One per line, with an optional name: 1234-5678-9012 prod-eu" error={error || undefined} errorRole="none">
        <textarea className="su-mono" rows={3} value={text} onChange={(ev) => setText(ev.target.value)} style={{ width: '100%', font: 'inherit', fontSize: 'var(--fs-md)', color: 'var(--text)', background: 'var(--surface-elevated)', border: '1px solid var(--border-control)', borderRadius: 'var(--radius-input)', padding: 'var(--sp-2) var(--sp-3)' }} />
      </Field>
      <p className="su-hint">Nothing from AWS counts until every production account is read or marked not production.</p>
      <div className="su-acts"><Button type="submit">Save accounts</Button></div>
    </>}
  </form>;
}

function SendForm(p: RowProps & { onDone: () => void }) {
  const options = pickerPeople(p.e, p.people, p.viewer.user_id);
  const [who, setWho] = useState(options[0]?.user_id ?? '');
  const [busy, setBusy] = useState(false);
  const sel = useRef<HTMLSelectElement>(null);
  useEffect(() => { sel.current?.focus(); }, []);
  if (!options.length) return <div className="su-inline-form" data-esc-owner=""><p className="su-hint">Nobody else in this workspace can do this step yet. Invite a teammate in Team & assignments.</p>
    <div className="su-acts"><Link to={`/w/${p.slug}/app/settings/team`}>Invite a teammate</Link><Button variant="ghost" onClick={p.onDone}>Close</Button></div></div>;
  const send = async () => {
    setBusy(true);
    const prev = p.e.assignee_user_id;
    const name = options.find((o) => o.user_id === who)?.name ?? 'them';
    try { await p.onPatch({ assignee_user_id: who }, `Sent to ${name}. It shows on their Today.`, { assignee_user_id: prev }, false); p.onDone(); }
    catch (err) { p.announce(err instanceof Error ? err.message : 'That did not send. Try again.'); }
    finally { setBusy(false); }
  };
  return <div className="su-inline-form" role="group" aria-label="Send this step" data-esc-owner=""
    onKeyDown={(ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); p.onDone(); } }}>
    <div className="su-acts" style={{ alignItems: 'end' }}>
      <Field id={`send-${p.e.id}`} label="Send to"><Select ref={sel} value={who} onChange={(ev) => setWho(ev.target.value)}>
        {options.map((o) => <option key={o.user_id} value={o.user_id}>{o.name} · {o.role}</option>)}
      </Select></Field>
      <Button onClick={() => void send()} disabled={busy}>{busy ? 'Sending…' : 'Send'}</Button>
      <Button variant="ghost" onClick={p.onDone}>Cancel</Button>
    </div>
    <p className="su-hint">It shows on their Today, with the steps.</p>
  </div>;
}

const REASONS = ['Waiting on legal or security review', 'Not this quarter', 'We handle this another way', "Doesn't apply to us", 'Other'];

/** Snooze vs decline: nothing preselected, a reason required, errors tied to their fields (R2-11). */
function SkipForm(p: RowProps & { onCancel: () => void }) {
  const [mode, setMode] = useState<'snooze' | 'decline' | ''>('');
  const [why, setWhy] = useState('');
  const [note, setNote] = useState('');
  const [until, setUntil] = useState(() => new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10));
  const [errors, setErrors] = useState<{ mode?: string; why?: string; until?: string; form?: string }>({});
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const next: typeof errors = {};
    if (!mode) next.mode = 'Choose snooze or decline.';
    if (!why) next.why = 'Choose a reason.';
    if (mode === 'snooze' && !(until > new Date().toISOString().slice(0, 10))) next.until = 'Choose a date after today.';
    setErrors(next);
    if (Object.keys(next).length) {
      p.announce(Object.values(next).join(' '));
      (next.mode ? first.current : document.getElementById(next.why ? `skip-why-${p.e.id}` : `skip-until-${p.e.id}`))?.focus();
      return;
    }
    const reason = note.trim() ? `${why}: ${note.trim()}` : why;
    try {
      if (mode === 'snooze') await p.onPatch({ state: 'snoozed', until, reason }, `Snoozed until ${new Date(`${until}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}. Reason saved.`, { state: 'reopen' }, true);
      else await p.onPatch({ state: 'declined', reason }, 'Declined. Reason saved.', { state: 'reopen' }, true);
    } catch (err) { setErrors({ form: err instanceof Error ? err.message : 'That did not save. Try again.' }); }
  };
  return <form className="su-inline-form" aria-label="Skip this step" noValidate onSubmit={submit} data-esc-owner=""
    onKeyDown={(ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); p.onCancel(); } }}>
    <fieldset aria-describedby={errors.mode ? `skip-mode-err-${p.e.id}` : undefined}>
      <legend>Skip it how? (required)</legend>
      <label className="su-radio"><input ref={first} type="radio" name={`skip-${p.e.id}`} checked={mode === 'snooze'} aria-invalid={errors.mode ? true : undefined} onChange={() => { setMode('snooze'); setErrors((x) => ({ ...x, mode: undefined })); }} />Snooze until a date</label>
      <label className="su-radio"><input type="radio" name={`skip-${p.e.id}`} checked={mode === 'decline'} aria-invalid={errors.mode ? true : undefined} onChange={() => { setMode('decline'); setErrors((x) => ({ ...x, mode: undefined })); }} />Decline (until someone reopens it)</label>
      {errors.mode && <p className="su-err" id={`skip-mode-err-${p.e.id}`}>{errors.mode}</p>}
    </fieldset>
    <div className="su-form-grid">
      {mode === 'snooze' && <Field id={`skip-until-${p.e.id}`} label="Bring it back on" error={errors.until} errorRole="none"><Input type="date" value={until} onChange={(ev) => setUntil(ev.target.value)} /></Field>}
      <Field id={`skip-why-${p.e.id}`} label="Reason (required)" error={errors.why} errorRole="none">
        <Select value={why} onChange={(ev) => { setWhy(ev.target.value); setErrors((x) => ({ ...x, why: undefined })); }}>
          <option value="">Choose a reason</option>
          {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
      </Field>
      <Field id={`skip-note-${p.e.id}`} label="Note (shown on the step)"><Input value={note} maxLength={300} onChange={(ev) => setNote(ev.target.value)} autoComplete="off" /></Field>
    </div>
    {errors.form && <p className="su-err" role="alert">{errors.form}</p>}
    <div className="su-acts"><Button type="submit">Save</Button><Button variant="ghost" onClick={p.onCancel}>Cancel</Button></div>
  </form>;
}
